import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { customersAPI, projectsAPI, offersAPI, legacyOffersAPI } from "../lib/api";
import { api, getErrorMessage } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import IncognitoBadge from "../components/IncognitoBadge";
import { Separator } from "../components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft,
  Edit,
  Save,
  X,
  Mail,
  Phone,
  MapPin,
  Building2,
  CreditCard,
  FileText,
  Plus,
  Calendar,
  CalendarPlus,
  Send,
  Download,
  Loader2,
  Clock,
  Home,
  Upload,
  Archive,
  MessageSquare
} from "lucide-react";
import CustomerMessaging from "../components/CustomerMessaging";

const statusLabels = {
  neu: "Neu",
  angebot_erstellt: "Angebot erstellt",
  angebot_angenommen: "Angenommen",
  angebot_abgelehnt: "Abgelehnt",
  planung: "In Planung",
  montage_geplant: "Montage geplant",
  in_montage: "In Montage",
  abgeschlossen: "Abgeschlossen"
};

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [projects, setProjects] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  
  // Appointment confirmation state
  const [showAppointmentDialog, setShowAppointmentDialog] = useState(false);
  const [sendingAppointment, setSendingAppointment] = useState(false);
  const [appointmentData, setAppointmentData] = useState({
    date: "",
    time: "10:00",
    duration_minutes: 60,
    message: "",
    location_note: "",
    appointment_type: "onsite"  // "onsite" = Vor-Ort-Termin, "phone" = Telefontermin
  });
  const [lastIcsContent, setLastIcsContent] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [legacyOffers, setLegacyOffers] = useState([]);
  const [loadingLegacy, setLoadingLegacy] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      // Load customer and their projects in parallel
      // Use customer_id filter to only fetch this customer's projects (not all)
      const [customerRes, projectsRes] = await Promise.all([
        customersAPI.get(id),
        projectsAPI.list(null, false, id)  // null=all status, false=no archived, id=customer_id
      ]);
      
      setCustomer(customerRes.data);
      setFormData(customerRes.data);
      
      const customerProjects = projectsRes.data;
      setProjects(customerProjects);
      
      // Load offers for all projects in parallel (instead of sequential)
      if (customerProjects.length > 0) {
        const offersPromises = customerProjects.map(project => offersAPI.list(project.id));
        const offersResults = await Promise.all(offersPromises);
        const allOffers = offersResults.flatMap(res => res.data);
        setOffers(allOffers);
      } else {
        setOffers([]);
      }
      
      // Load legacy offers
      loadLegacyOffers();
      
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Fehler beim Laden der Kundendaten");
      navigate("/kunden");
    } finally {
      setLoading(false);
    }
  };

  const loadLegacyOffers = async () => {
    try {
      setLoadingLegacy(true);
      const response = await legacyOffersAPI.list({ customer_id: id });
      setLegacyOffers(response.data.data || []);
    } catch (error) {
      console.error("Failed to load legacy offers:", error);
    } finally {
      setLoadingLegacy(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        salutation: formData.salutation,
        first_name: formData.first_name,
        last_name: formData.last_name,
        address: formData.address,
        phone: formData.phone || "",
        email: formData.email || "",
        tax_number: formData.tax_number || "",
        bank_iban: formData.bank_iban || "",
        bank_bic: formData.bank_bic || "",
        bank_name: formData.bank_name || ""
      };
      
      const response = await customersAPI.update(id, updateData);
      setCustomer(response.data);
      setEditing(false);
      toast.success("Kunde aktualisiert");
    } catch (error) {
      console.error("Failed to update customer:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateProject = async () => {
    try {
      await projectsAPI.create({
        customer_id: id,
        notes: ""
      });
      toast.success("Neues Projekt angelegt");
      loadData();
    } catch (error) {
      console.error("Failed to create project:", error);
      toast.error("Fehler beim Anlegen des Projekts");
    }
  };

  // Appointment confirmation functions
  const openAppointmentDialog = () => {
    // Set default date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const defaultDate = tomorrow.toISOString().split('T')[0];
    
    setAppointmentData({
      date: defaultDate,
      time: "10:00",
      duration_minutes: 60,
      message: "",
      location_note: ""
    });
    setShowAppointmentDialog(true);
  };

  const handleSendAppointment = async () => {
    if (!appointmentData.date || !appointmentData.time) {
      toast.error("Bitte Datum und Uhrzeit eingeben");
      return;
    }
    
    if (!customer.email) {
      toast.error("Kunde hat keine E-Mail-Adresse");
      return;
    }
    
    setSendingAppointment(true);
    try {
      // axios uses XHR → bypasses the Emergent platform fetch-wrapper that consumes 4xx bodies
      const response = await api.post(`/customers/${id}/send-appointment`, appointmentData);
      const result = response.data;

      // Store ICS content for download (available even if email failed)
      if (result.ics_content) {
        setLastIcsContent({
          content: result.ics_content,
          filename: result.ics_filename
        });
      }
      
      // Check if email was sent successfully
      if (result.success) {
        toast.success(`Terminbestätigung an ${customer.email} gesendet!`);
      } else {
        // Email failed but ICS was created - show warning with download option
        toast.warning(result.message || "E-Mail konnte nicht gesendet werden, aber die Kalenderdatei wurde erstellt.");
      }
      
      setShowAppointmentDialog(false);
      
      // Reload appointments
      loadAppointments();
      
    } catch (error) {
      console.error("Failed to send appointment:", error?.response?.status, error?.response?.data);
      const msg = getErrorMessage(error, "Fehler beim Senden der Terminbestätigung");
      toast.error("Fehler beim Senden der Terminbestätigung", { description: msg, duration: 10000 });
    } finally {
      setSendingAppointment(false);
    }
  };

  const loadAppointments = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem("token");
      
      const response = await fetch(`${API_URL}/api/customers/${id}/appointments`, {
        headers: { "Authorization": `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAppointments(data);
      }
    } catch (error) {
      console.warn("Could not load appointments:", error);
    }
  };

  const downloadIcs = (icsContent, filename) => {
    const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Load appointments when customer loads
  useEffect(() => {
    if (customer) {
      loadAppointments();
    }
  }, [customer]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!customer) return null;

  return (
    <div className="space-y-6 animate-slide-in" data-testid="customer-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/kunden">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold font-['Manrope']">
              {customer.salutation} {customer.first_name} {customer.last_name}
            </h1>
            <Badge variant="outline" className="font-mono">
              {customer.customer_number}
            </Badge>
          </div>
          <p className="text-muted-foreground flex items-center gap-1">
            <MapPin className="h-4 w-4" />
            {customer.address}
          </p>
        </div>
        {editing ? (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setEditing(false); setFormData(customer); }}>
              <X className="h-4 w-4 mr-2" />
              Abbrechen
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Speichern..." : "Speichern"}
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button 
              variant="default" 
              className="bg-green-600 hover:bg-green-700"
              onClick={openAppointmentDialog}
              data-testid="send-appointment-btn"
            >
              <CalendarPlus className="h-4 w-4 mr-2" />
              Termin bestätigen
            </Button>
            <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-customer-btn">
              <Edit className="h-4 w-4 mr-2" />
              Bearbeiten
            </Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="details">
        <TabsList>
          <TabsTrigger value="details">Kundendaten</TabsTrigger>
          <TabsTrigger value="projects">Projekte ({projects.length})</TabsTrigger>
          <TabsTrigger value="offers">Angebote ({offers.length})</TabsTrigger>
          <TabsTrigger value="legacy">Legacy ({legacyOffers.length})</TabsTrigger>
          <TabsTrigger value="appointments">Termine ({appointments.length})</TabsTrigger>
          <TabsTrigger value="messaging" className="flex items-center gap-1">
            <MessageSquare className="h-4 w-4" />
            SMS/WhatsApp
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Personal Data */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Persönliche Daten
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Anrede</Label>
                        <Select
                          value={formData.salutation}
                          onValueChange={(value) => setFormData({ ...formData, salutation: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Herr">Herr</SelectItem>
                            <SelectItem value="Frau">Frau</SelectItem>
                            <SelectItem value="Divers">Divers</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Vorname</Label>
                        <Input
                          value={formData.first_name}
                          onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Nachname</Label>
                        <Input
                          value={formData.last_name}
                          onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Adresse</Label>
                      <Input
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Telefon</Label>
                        <Input
                          value={formData.phone || ""}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>E-Mail</Label>
                        <Input
                          value={formData.email || ""}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-lg font-semibold text-primary">
                          {customer.first_name?.[0]}{customer.last_name?.[0]}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">
                          {customer.salutation} {customer.first_name} {customer.last_name}
                        </p>
                        <p className="text-sm text-muted-foreground font-mono">
                          {customer.customer_number}
                        </p>
                      </div>
                    </div>
                    <Separator />
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span>{customer.address}</span>
                      </div>
                      {customer.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone className="h-4 w-4 text-muted-foreground" />
                          <a href={`tel:${customer.phone}`} className="hover:underline">
                            {customer.phone}
                          </a>
                        </div>
                      )}
                      {customer.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          <a href={`mailto:${customer.email}`} className="hover:underline">
                            {customer.email}
                          </a>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Bank & Tax Data */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Steuer & Bankverbindung
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div className="space-y-2">
                      <Label>Steuernummer</Label>
                      <Input
                        value={formData.tax_number || ""}
                        onChange={(e) => setFormData({ ...formData, tax_number: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2 col-span-2">
                        <Label>IBAN</Label>
                        <Input
                          value={formData.bank_iban || ""}
                          onChange={(e) => setFormData({ ...formData, bank_iban: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>BIC</Label>
                        <Input
                          value={formData.bank_bic || ""}
                          onChange={(e) => setFormData({ ...formData, bank_bic: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Bank</Label>
                        <Input
                          value={formData.bank_name || ""}
                          onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Steuernummer</span>
                      <span className="font-mono">{customer.tax_number || "-"}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">IBAN</span>
                      <span className="font-mono">{customer.bank_iban || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">BIC</span>
                      <span className="font-mono">{customer.bank_bic || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Bank</span>
                      <span>{customer.bank_name || "-"}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="projects" className="mt-6">
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">Projekte</h3>
              <Button onClick={handleCreateProject} data-testid="new-project-btn">
                <Plus className="h-4 w-4 mr-2" />
                Neues Projekt
              </Button>
            </div>
            
            {projects.length > 0 ? (
              <div className="grid gap-4">
                {projects.map((project) => (
                  <Card key={project.id} className="card-hover">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium font-mono">{project.project_number}</p>
                            <IncognitoBadge active={project.is_incognito} />
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Erstellt am {new Date(project.created_at).toLocaleDateString("de-DE")}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={`status-${project.status}`}>
                            {statusLabels[project.status] || project.status}
                          </Badge>
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/angebote/neu/${project.id}`}>
                              <FileText className="h-4 w-4 mr-2" />
                              Angebot erstellen
                            </Link>
                          </Button>
                        </div>
                      </div>
                      {project.installation_date && (
                        <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          Montagetermin: {new Date(project.installation_date).toLocaleDateString("de-DE")}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="py-8">
                <CardContent className="text-center">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Noch keine Projekte vorhanden</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        <TabsContent value="offers" className="mt-6">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Angebote</h3>
            
            {offers.length > 0 ? (
              <div className="grid gap-4">
                {offers.map((offer) => (
                  <Card key={offer.id} className="card-hover">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium font-mono">{offer.offer_number}</p>
                            <IncognitoBadge active={offer.is_incognito} />
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {typeof offer.kwp === 'number' ? offer.kwp.toFixed(2) : offer.kwp} kWp | {offer.storage_size} kWh Speicher
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <p className="font-bold text-lg font-['Manrope']">
                              {new Intl.NumberFormat("de-DE", {
                                style: "currency",
                                currency: "EUR"
                              }).format(offer.display_price)}
                            </p>
                            <Badge className={`status-${offer.status === "angenommen" ? "angebot_angenommen" : offer.status === "abgelehnt" ? "angebot_abgelehnt" : "angebot_erstellt"}`}>
                              {offer.status === "erstellt" ? "Offen" : offer.status === "angenommen" ? "Angenommen" : "Abgelehnt"}
                            </Badge>
                          </div>
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/angebote/${offer.id}`}>
                              Details
                            </Link>
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="py-8">
                <CardContent className="text-center">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">Noch keine Angebote vorhanden</p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Legacy-Angebote Tab */}
        <TabsContent value="legacy" className="space-y-6 mt-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Legacy-Angebote / Freitextangebote</h3>
            <div className="flex gap-2">
              <Button variant="outline" asChild>
                <Link to={`/legacy-angebote/neu/${id}`} data-testid="btn-new-legacy-offer">
                  <Plus className="h-4 w-4 mr-2" />
                  Neues Freitextangebot
                </Link>
              </Button>
              <Button asChild data-testid="btn-import-legacy">
                <Link to={`/legacy-angebote/import/${id}`}>
                  <Upload className="h-4 w-4 mr-2" />
                  Dokument importieren
                </Link>
              </Button>
            </div>
          </div>
          
          {loadingLegacy ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : legacyOffers.length > 0 ? (
            <div className="grid gap-4">
              {legacyOffers.map((legacy) => (
                <Card key={legacy.id} className="card-hover">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{legacy.title || "Ohne Titel"}</p>
                        <p className="text-sm text-muted-foreground">
                          {legacy.offer_reference && `${legacy.offer_reference} • `}
                          {legacy.document_type === "auftragsbestätigung" ? "Auftragsbestätigung" : "Angebot"}
                          {legacy.document_date && ` • ${new Date(legacy.document_date).toLocaleDateString("de-DE")}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-bold text-lg">
                            {new Intl.NumberFormat("de-DE", {
                              style: "currency",
                              currency: "EUR"
                            }).format(legacy.gross_amount || 0)}
                          </p>
                          <Badge className={
                            legacy.status === "vollständig_fakturiert" ? "bg-emerald-100 text-emerald-800" :
                            legacy.status === "teilfakturiert" ? "bg-amber-100 text-amber-800" :
                            legacy.status === "freigegeben" ? "bg-green-100 text-green-800" :
                            legacy.status === "geprüft" ? "bg-blue-100 text-blue-800" :
                            "bg-gray-100 text-gray-800"
                          }>
                            {legacy.status === "importiert" && "Importiert"}
                            {legacy.status === "geprüft" && "Geprüft"}
                            {legacy.status === "freigegeben" && "Freigegeben"}
                            {legacy.status === "beauftragt" && "Beauftragt"}
                            {legacy.status === "teilfakturiert" && "Teilfakturiert"}
                            {legacy.status === "vollständig_fakturiert" && "Vollst. fakturiert"}
                          </Badge>
                        </div>
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/legacy-angebote/${legacy.id}`}>
                            Details
                          </Link>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="py-8">
              <CardContent className="text-center">
                <Archive className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground mb-2">Noch keine Legacy-Angebote vorhanden</p>
                <p className="text-sm text-muted-foreground">
                  Importieren Sie alte Angebote aus Ihrer bisherigen Software oder erstellen Sie ein neues Freitextangebot.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Termine Tab */}
        <TabsContent value="appointments" className="space-y-6 mt-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Gesendete Terminbestätigungen</h3>
            <Button onClick={openAppointmentDialog} className="bg-green-600 hover:bg-green-700">
              <CalendarPlus className="h-4 w-4 mr-2" />
              Neuer Termin
            </Button>
          </div>
          
          {appointments.length > 0 ? (
            <div className="space-y-4">
              {appointments.map((apt) => (
                <Card key={apt.id}>
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Appointment Type Badge */}
                          {apt.appointment_type === "phone" ? (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-300">
                              <Phone className="h-3 w-3 mr-1" />
                              Telefontermin
                            </Badge>
                          ) : (
                            <Badge className="bg-blue-100 text-blue-800 border border-blue-300">
                              <Home className="h-3 w-3 mr-1" />
                              Vor-Ort-Termin
                            </Badge>
                          )}
                          <span className="font-medium">
                            {new Date(apt.date).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </span>
                          <Badge variant="outline">{apt.time} Uhr</Badge>
                          <Badge variant="secondary">{apt.duration_minutes} Min.</Badge>
                        </div>
                        {apt.location_note && (
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {apt.location_note}
                          </p>
                        )}
                        {apt.message && (
                          <p className="text-sm text-muted-foreground">{apt.message}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Gesendet am {new Date(apt.sent_at).toLocaleString('de-DE')} von {apt.created_by_name}
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => downloadIcs(apt.ics_content, `Termin_${apt.date}.ics`)}
                      >
                        <Download className="h-4 w-4 mr-1" />
                        ICS
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="py-8">
              <CardContent className="text-center">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">Noch keine Termine gesendet</p>
                <Button 
                  className="mt-4 bg-green-600 hover:bg-green-700"
                  onClick={openAppointmentDialog}
                >
                  <CalendarPlus className="h-4 w-4 mr-2" />
                  Ersten Termin bestätigen
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Messaging Tab */}
        <TabsContent value="messaging" className="mt-6">
          <CustomerMessaging 
            customerId={id} 
            customer={customer}
            onCustomerUpdate={setCustomer}
          />
        </TabsContent>
      </Tabs>

      {/* Appointment Confirmation Dialog */}
      <Dialog open={showAppointmentDialog} onOpenChange={setShowAppointmentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarPlus className="h-5 w-5 text-green-600" />
              Terminbestätigung senden
            </DialogTitle>
            <DialogDescription>
              Senden Sie dem Kunden eine Terminbestätigung per E-Mail mit Kalendereintrag.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Customer info */}
            <div className="bg-muted/50 p-3 rounded-lg text-sm">
              <div className="font-medium">{customer?.first_name} {customer?.last_name}</div>
              <div className="text-muted-foreground">{customer?.email || "Keine E-Mail"}</div>
              <div className="text-muted-foreground">{customer?.address}</div>
            </div>

            {!customer?.email && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
                Achtung: Dieser Kunde hat keine E-Mail-Adresse hinterlegt!
              </div>
            )}
            
            {/* Appointment Type Selection */}
            <div className="space-y-2">
              <Label>Terminart</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={appointmentData.appointment_type === "onsite" ? "default" : "outline"}
                  className={`flex-1 ${appointmentData.appointment_type === "onsite" ? "bg-blue-600 hover:bg-blue-700" : ""}`}
                  onClick={() => setAppointmentData({...appointmentData, appointment_type: "onsite"})}
                >
                  <Home className="h-4 w-4 mr-2" />
                  Vor-Ort-Termin
                </Button>
                <Button
                  type="button"
                  variant={appointmentData.appointment_type === "phone" ? "default" : "outline"}
                  className={`flex-1 ${appointmentData.appointment_type === "phone" ? "bg-amber-600 hover:bg-amber-700" : ""}`}
                  onClick={() => setAppointmentData({...appointmentData, appointment_type: "phone"})}
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Telefontermin
                </Button>
              </div>
              {appointmentData.appointment_type === "phone" && (
                <p className="text-xs text-muted-foreground">
                  📞 Bei einem Telefontermin wird der Kunde darauf hingewiesen, dass er angerufen wird.
                </p>
              )}
            </div>
            
            {/* Date */}
            <div className="space-y-2">
              <Label htmlFor="apt-date">Datum</Label>
              <Input
                id="apt-date"
                type="date"
                value={appointmentData.date}
                onChange={(e) => setAppointmentData({...appointmentData, date: e.target.value})}
              />
            </div>
            
            {/* Time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="apt-time">Uhrzeit</Label>
                <Input
                  id="apt-time"
                  type="time"
                  value={appointmentData.time}
                  onChange={(e) => setAppointmentData({...appointmentData, time: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="apt-duration">Dauer (Min.)</Label>
                <Select 
                  value={String(appointmentData.duration_minutes)}
                  onValueChange={(v) => setAppointmentData({...appointmentData, duration_minutes: parseInt(v)})}
                >
                  <SelectTrigger id="apt-duration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="30">30 Minuten</SelectItem>
                    <SelectItem value="60">1 Stunde</SelectItem>
                    <SelectItem value="90">1,5 Stunden</SelectItem>
                    <SelectItem value="120">2 Stunden</SelectItem>
                    <SelectItem value="180">3 Stunden</SelectItem>
                    <SelectItem value="240">4 Stunden</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Location note */}
            <div className="space-y-2">
              <Label htmlFor="apt-location">Ort-Hinweis (optional)</Label>
              <Input
                id="apt-location"
                placeholder="z.B. Eingang über Hinterhof"
                value={appointmentData.location_note}
                onChange={(e) => setAppointmentData({...appointmentData, location_note: e.target.value})}
              />
            </div>
            
            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="apt-message">Zusätzliche Nachricht (optional)</Label>
              <Textarea
                id="apt-message"
                placeholder="z.B. Bitte halten Sie Stromrechnungen bereit..."
                value={appointmentData.message}
                onChange={(e) => setAppointmentData({...appointmentData, message: e.target.value})}
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowAppointmentDialog(false)} disabled={sendingAppointment}>
              Abbrechen
            </Button>
            <Button 
              onClick={handleSendAppointment}
              disabled={sendingAppointment || !customer?.email || !appointmentData.date || !appointmentData.time}
              className="bg-green-600 hover:bg-green-700"
            >
              {sendingAppointment ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Wird gesendet...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Terminbestätigung senden
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ICS Download Toast */}
      {lastIcsContent && (
        <div className="fixed bottom-4 right-4 bg-white border shadow-lg rounded-lg p-4 max-w-sm z-50">
          <div className="flex items-start gap-3">
            <Calendar className="h-5 w-5 text-green-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium">Termin gesendet!</h4>
              <p className="text-sm text-muted-foreground mb-3">
                Möchten Sie den Termin auch in Ihren eigenen Kalender importieren?
              </p>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  onClick={() => {
                    downloadIcs(lastIcsContent.content, lastIcsContent.filename);
                    setLastIcsContent(null);
                  }}
                >
                  <Download className="h-4 w-4 mr-1" />
                  ICS herunterladen
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost"
                  onClick={() => setLastIcsContent(null)}
                >
                  Schließen
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
